import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Check, Phone, Mail, Users, FileText, TrendingUp, ChevronRight, Save } from 'lucide-react';
import { workflow as workflowApi, filenotesV2 as filenotesApi, points as pointsApi, checklist as checklistApi, anzsco as anzscoApi, matters as mattersApi } from '@/lib/api';
import { toast } from 'sonner';

// ============================================================================
// Workflow Stage Tracker — 14-stage ladder with current stage + click-to-advance.
// ============================================================================
export function WorkflowTracker({ matter, onChanged }) {
  const [stages, setStages] = React.useState([]);
  const [history, setHistory] = React.useState([]);
  const [busy, setBusy] = React.useState(null);
  const [noteOpen, setNoteOpen] = React.useState(null);
  const [note, setNote] = React.useState('');

  const load = React.useCallback(() => {
    Promise.all([workflowApi.stages(), workflowApi.history(matter.id)])
      .then(([s, h]) => { setStages(s.items || []); setHistory(h.history || []); })
      .catch(() => {});
  }, [matter.id]);
  React.useEffect(load, [load]);

  const advance = async (toStage) => {
    setBusy(toStage);
    try {
      await workflowApi.setStage(matter.id, toStage, note.trim() || null);
      toast.success(`Stage updated → ${toStage}`);
      setNoteOpen(null); setNote('');
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(null); }
  };

  const currentIdx = stages.findIndex((s) => s.key === matter.stage);
  return (
    <Card data-testid="workflow-tracker">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
          <TrendingUp className="h-4 w-4 text-emerald-600" /> Migration workflow
          <span className="ml-auto text-xs text-slate-500">Stage {Math.max(currentIdx, 0) + 1}/{stages.length || 14}</span>
        </div>
        <div className="flex items-center gap-1 overflow-x-auto py-2">
          {stages.map((s, i) => {
            const done = i < currentIdx;
            const current = i === currentIdx;
            return (
              <React.Fragment key={s.key}>
                <button
                  onClick={() => setNoteOpen(s.key)}
                  title={s.hint}
                  data-testid={`workflow-stage-${s.key}`}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition ${
                    current ? 'bg-emerald-500 text-white shadow-sm' :
                    done ? 'bg-emerald-100 text-emerald-700' :
                    'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
                >
                  {done && <Check className="mr-1 inline h-3 w-3" />}{s.label}
                </button>
                {i < stages.length - 1 && <ChevronRight className="h-3 w-3 flex-shrink-0 text-slate-300" />}
              </React.Fragment>
            );
          })}
        </div>
        {history.length > 0 && (
          <details className="mt-3 text-xs text-slate-500">
            <summary className="cursor-pointer hover:text-slate-700">Stage history ({history.length})</summary>
            <div className="mt-2 space-y-1">
              {history.slice().reverse().map((h) => (
                <div key={h.id} className="flex items-center gap-2 py-1" data-testid={`workflow-history-${h.id}`}>
                  <span className="text-slate-400">{new Date(h.at).toLocaleString('en-AU')}</span>
                  <span>· {h.by_name}: <b>{h.from || '—'}</b> → <b>{h.to}</b></span>
                  {h.note && <span className="italic">— {h.note}</span>}
                </div>
              ))}
            </div>
          </details>
        )}
        <Dialog open={!!noteOpen} onOpenChange={(v) => !v && setNoteOpen(null)}>
          <DialogContent data-testid="workflow-advance-dialog">
            <DialogHeader><DialogTitle>Move to stage: {stages.find((s) => s.key === noteOpen)?.label}</DialogTitle></DialogHeader>
            <div className="text-xs text-slate-500">{stages.find((s) => s.key === noteOpen)?.hint}</div>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note (audit-logged)…" data-testid="workflow-advance-note" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setNoteOpen(null)}>Cancel</Button>
              <Button onClick={() => advance(noteOpen)} disabled={busy === noteOpen} data-testid="workflow-advance-submit">
                {busy === noteOpen && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Move to this stage
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Applicant Details Editor — DOB, passport, phone, offshore/onshore, current visa.
// ============================================================================
export function ApplicantDetailsCard({ matter, onSaved }) {
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [bodySuggestion, setBodySuggestion] = React.useState(null);

  React.useEffect(() => {
    if (open) setForm({
      date_of_birth: matter.date_of_birth || '',
      passport_number: matter.passport_number || '',
      passport_expiry: matter.passport_expiry || '',
      phone: matter.phone || '',
      second_email: matter.second_email || '',
      emergency_contact_name: matter.emergency_contact_name || '',
      emergency_contact_phone: matter.emergency_contact_phone || '',
      emergency_contact_relationship: matter.emergency_contact_relationship || '',
      offshore_or_onshore: matter.offshore_or_onshore || '',
      current_visa: matter.current_visa || '',
      visa_expiry: matter.visa_expiry || '',
      anzsco_code: matter.anzsco_code || '',
      occupation: matter.occupation || '',
      assessing_body: matter.assessing_body || '',
    });
  }, [open, matter]);

  const onAnzscoChange = async (code) => {
    setForm((f) => ({ ...f, anzsco_code: code }));
    if (code && code.length >= 3) {
      try {
        const r = await anzscoApi.suggestBody(code);
        setBodySuggestion(r);
        if (r?.primary && !form.assessing_body) setForm((f) => ({ ...f, assessing_body: r.primary }));
      } catch { /* silent */ }
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const patch = {};
      Object.entries(form).forEach(([k, v]) => { if (v !== '' && v !== null) patch[k] = v; });
      await mattersApi.update(matter.id, patch);
      toast.success('Applicant details saved');
      onSaved?.(); setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const kv = (label, val) => (
    <div><div className="text-xs text-slate-400">{label}</div><div className="text-sm text-[#1A3A5C]">{val || '—'}</div></div>
  );

  return (
    <Card data-testid="applicant-details-card">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
          <Users className="h-4 w-4 text-blue-600" /> Applicant details
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => setOpen(true)} data-testid="applicant-details-edit">Edit</Button>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {kv('Date of birth', matter.date_of_birth)}
          {kv('Passport', matter.passport_number)}
          {kv('Passport expires', matter.passport_expiry)}
          {kv('Phone', matter.phone)}
          {kv('Second email', matter.second_email)}
          {kv('Offshore/Onshore', matter.offshore_or_onshore)}
          {kv('Current visa', matter.current_visa)}
          {kv('Visa expires', matter.visa_expiry)}
          {kv('Occupation', matter.occupation)}
          {kv('ANZSCO', matter.anzsco_code)}
          {kv('Assessing body', matter.assessing_body)}
          {kv('Emergency contact', matter.emergency_contact_name ? `${matter.emergency_contact_name}${matter.emergency_contact_phone ? ' · ' + matter.emergency_contact_phone : ''}` : null)}
        </div>

        <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="applicant-edit-dialog">
            <DialogHeader><DialogTitle>Edit applicant details</DialogTitle></DialogHeader>
            <div className="grid gap-3 md:grid-cols-2">
              <Fld label="Date of birth" value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} type="date" testid="applicant-dob" />
              <Fld label="Passport number" value={form.passport_number} onChange={(v) => setForm({ ...form, passport_number: v })} testid="applicant-passport" />
              <Fld label="Passport expiry" value={form.passport_expiry} onChange={(v) => setForm({ ...form, passport_expiry: v })} type="date" />
              <Fld label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} type="tel" testid="applicant-phone" />
              <Fld label="Second email" value={form.second_email} onChange={(v) => setForm({ ...form, second_email: v })} type="email" testid="applicant-second-email" />
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Offshore / Onshore</label>
                <Select value={form.offshore_or_onshore || ''} onValueChange={(v) => setForm({ ...form, offshore_or_onshore: v })}>
                  <SelectTrigger data-testid="applicant-offshore-onshore"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="offshore">Offshore</SelectItem>
                    <SelectItem value="onshore">Onshore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Fld label="Current visa" value={form.current_visa} onChange={(v) => setForm({ ...form, current_visa: v })} testid="applicant-current-visa" placeholder="e.g. 500, BVA, BVE" />
              <Fld label="Visa expiry" value={form.visa_expiry} onChange={(v) => setForm({ ...form, visa_expiry: v })} type="date" testid="applicant-visa-expiry" />
              <Fld label="Occupation" value={form.occupation} onChange={(v) => setForm({ ...form, occupation: v })} />
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">ANZSCO code</label>
                <Input value={form.anzsco_code} onChange={(e) => onAnzscoChange(e.target.value)} placeholder="221111" data-testid="applicant-anzsco" />
                {bodySuggestion && (
                  <div className="mt-1 text-xs text-emerald-700" data-testid="applicant-body-suggestion">
                    Suggested body: <b>{bodySuggestion.primary}</b>
                    {bodySuggestion.options.length > 1 && ` (or ${bodySuggestion.options.slice(1).join(', ')})`}
                    <span className="ml-1 text-slate-400">· {bodySuggestion.match}</span>
                  </div>
                )}
              </div>
              <Fld label="Assessing body" value={form.assessing_body} onChange={(v) => setForm({ ...form, assessing_body: v })} placeholder="CPA Australia" />
              <Fld label="Emergency contact name" value={form.emergency_contact_name} onChange={(v) => setForm({ ...form, emergency_contact_name: v })} testid="applicant-emergency-name" />
              <Fld label="Emergency contact phone" value={form.emergency_contact_phone} onChange={(v) => setForm({ ...form, emergency_contact_phone: v })} type="tel" />
              <Fld label="Emergency contact relationship" value={form.emergency_contact_relationship} onChange={(v) => setForm({ ...form, emergency_contact_relationship: v })} placeholder="Spouse, Parent, …" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={busy} data-testid="applicant-edit-submit">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

const Fld = ({ label, value, onChange, type = 'text', testid, placeholder }) => (
  <div>
    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
    <Input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
  </div>
);

// ============================================================================
// Points-Claim Widget — running total + one-per-category enforcement.
// ============================================================================
export function PointsWidget({ matterId }) {
  const [claim, setClaim] = React.useState({ selected_ids: [], items: [], total: 0 });
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(() => {
    pointsApi.get(matterId).then((d) => setClaim(d)).catch(() => {});
  }, [matterId]);
  React.useEffect(load, [load]);

  const toggle = async (rowId, category) => {
    let next = [...claim.selected_ids].filter((x) => x !== rowId);
    if (!claim.selected_ids.includes(rowId)) {
      // Adding — first purge any other in the same category
      const sameCat = claim.items.filter((i) => i.category === category).map((i) => i.id);
      next = next.filter((x) => !sameCat.includes(x));
      next.push(rowId);
    }
    setBusy(true);
    try {
      const r = await pointsApi.save(matterId, next);
      setClaim({ ...claim, selected_ids: r.selected_ids, total: r.total });
    } catch (e) { toast.error('Save failed'); }
    finally { setBusy(false); }
  };

  const byCat = {};
  (claim.items || []).forEach((i) => { (byCat[i.category] = byCat[i.category] || []).push(i); });
  const totalOK = claim.total >= 65;

  return (
    <Card data-testid="points-widget">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
          <TrendingUp className="h-4 w-4 text-blue-600" /> Points claim
          <span className={`ml-auto rounded-full px-3 py-1 text-sm font-bold ${totalOK ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-testid="points-total">
            {claim.total} points
          </span>
        </div>
        <div className="text-xs text-slate-500">Tick one per category. Best-of automatically chosen if you re-tick. GSM pool minimum is 65.</div>
        <div className="mt-3 space-y-3">
          {Object.entries(byCat).map(([cat, items]) => (
            <div key={cat} data-testid={`points-cat-${cat}`}>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">{cat.replace(/_/g, ' ')}</div>
              <div className="grid gap-1 md:grid-cols-2">
                {items.map((row) => {
                  const on = claim.selected_ids.includes(row.id);
                  return (
                    <label key={row.id} className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm cursor-pointer transition ${on ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'}`} data-testid={`points-row-${row.id}`}>
                      <Checkbox checked={on} onCheckedChange={() => !busy && toggle(row.id, row.category)} />
                      <span className="flex-1 text-[#1A3A5C]">{row.label}</span>
                      <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-700">+{row.points}</Badge>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Add-Filenote dialog + button — email / phone / client_visit / meeting / sms.
// ============================================================================
export function AddFilenoteButton({ matterId, onAdded }) {
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({ kind: 'phone', subject: '', body: '', duration_minutes: '' });
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    if (!form.subject.trim() || !form.body.trim()) { toast.error('Subject and body are required'); return; }
    setBusy(true);
    try {
      const payload = { kind: form.kind, subject: form.subject.trim(), body: form.body.trim() };
      if (form.duration_minutes) payload.duration_minutes = parseInt(form.duration_minutes, 10);
      await filenotesApi.add(matterId, payload);
      toast.success('Filenote saved');
      setForm({ kind: 'phone', subject: '', body: '', duration_minutes: '' });
      setOpen(false); onAdded?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  const iconFor = (k) => k === 'email' ? Mail : k === 'phone' ? Phone : k === 'client_visit' || k === 'meeting' ? Users : FileText;
  const Icon = iconFor(form.kind);
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="add-filenote-btn">
        <FileText className="mr-2 h-4 w-4" /> Add filenote
      </Button>
      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent data-testid="filenote-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Icon className="h-4 w-4" /> Add filenote</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Interaction type</label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="filenote-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="phone">Phone call</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="client_visit">Client visit</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Subject</label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Discussed passport upload" data-testid="filenote-subject" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Details</label>
              <Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="What was discussed / actioned…" data-testid="filenote-body" />
            </div>
            {(form.kind === 'phone' || form.kind === 'meeting' || form.kind === 'client_visit') && (
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Duration (minutes)</label>
                <Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} placeholder="15" data-testid="filenote-duration" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="filenote-submit">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================================
// Interactive Checklist — replaces the old auto-tick UI.
// ============================================================================
export function InteractiveChecklist({ matterId, items }) {
  const [state, setState] = React.useState({});
  const [busy, setBusy] = React.useState(null);

  React.useEffect(() => {
    checklistApi.get(matterId).then(setState).catch(() => setState({}));
  }, [matterId]);

  const toggle = async (id, label) => {
    const newDone = !state[id]?.done;
    setBusy(id);
    try {
      await checklistApi.tick(matterId, id, newDone, label);
      setState((s) => ({ ...s, [id]: { done: newDone, at: new Date().toISOString() } }));
    } catch (e) { toast.error('Failed'); }
    finally { setBusy(null); }
  };

  const doneCount = items.filter((i) => state[i.id]?.done).length;
  return (
    <div data-testid="interactive-checklist">
      <div className="mb-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
        <b className="text-[#1A3A5C]">{doneCount}/{items.length}</b> ticked · Items start unticked. Click to mark as received.
      </div>
      <div className="space-y-1">
        {items.map((it) => {
          const on = !!state[it.id]?.done;
          return (
            <label key={it.id} className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer transition ${on ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200 hover:bg-slate-50'}`} data-testid={`checklist-item-${it.id}`}>
              <Checkbox checked={on} disabled={busy === it.id} onCheckedChange={() => toggle(it.id, it.label)} />
              <div className="flex-1">
                <div className={`text-sm ${on ? 'text-slate-500 line-through' : 'text-[#1A3A5C]'}`}>{it.label}</div>
                {it.source && <div className="text-xs text-slate-400">Source: {it.source}</div>}
                {on && state[it.id]?.by && <div className="text-xs text-emerald-600">Ticked by {state[it.id].by} · {new Date(state[it.id].at).toLocaleString('en-AU')}</div>}
              </div>
              {busy === it.id && <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />}
            </label>
          );
        })}
      </div>
    </div>
  );
}
